/*
 *  -----------------------------------------------------------------------
 *    <copyright file="data-table.service.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import i18n from 'i18next';
import { formatDateUTC_DEPRECATED } from './convertor';
import { getPanels_OpraQueries_panels_Panel } from '@queries/generated/central-monitoring.generated';

const generateStringFromData = (data: any) => {
    let string = '';
    if (data.level) string += `${i18n.t('level')}: ${data.level}, `;
    if (data._dataField) string += `${i18n.t('admin:dataPoint')}: ${data._dataField}, `;
    if (data._dataSourceName) string += `${i18n.t('dataSource')}: ${data._dataSourceName}, `;
    if (data._denominatorField) string += `${i18n.t('admin:denominator')}: ${data._denominatorField}, `;
    if (data._numeratorField) string += `${i18n.t('admin:numerator')}: ${data._numeratorField}, `;

    if (data._panelName) string += `${i18n.t('admin:panel')}: ${data._panelName}, `;
    if (data._panelField && data._panelName)
        string += `${i18n.t('admin:calculation')}: ${i18n.t(`admin:${data._panelField}`)}, `;

    string = string.substr(0, string.length - 2);

    return string;
};

const formatColumn = (
    fieldsData: any[],
    dataSources: any[],
    item: any,
    index: number,
    panels?: getPanels_OpraQueries_panels_Panel[],
) => {
    const newItem = { ...item };

    newItem._dataField = fieldsData.find((el: any) => item.dataField === el.key)?.name ?? null;
    newItem._type = i18n.t(`admin:${item.type}`);
    newItem._numeratorField = fieldsData.find((el: any) => item.numeratorField === el.key)?.name ?? null;
    newItem._denominatorField = fieldsData.find((el: any) => item.denominatorField === el.key)?.name ?? null;
    newItem._dataSourceName = dataSources?.find((el: any) => el.id === item.dataSource)?.name ?? null;
    newItem._panelName = panels?.find((el: any) => el.id === item.panelId)?.graph?.plotTitle ?? null;
    newItem._panelId = item.panelId;
    newItem._panelField = item.panelField;

    return {
        ...newItem,
        index,
        id: index,
        _dataString: generateStringFromData(newItem),
    };
};

const createRiskOverviewGrid = (studyData: any, riskOverviews: any, sitesStatusesToExclude: string[], columns: any[]) =>
    riskOverviews.map((item: any) => {
        const element: any = {};

        element.siteToShow =
            !sitesStatusesToExclude || !item.siteStatus || sitesStatusesToExclude.indexOf(item.siteStatus) < 0;

        columns.forEach((el: any) => {
            element.id = item.siteId;
            if (el.field === 'numSubjects') {
                // Get the site from the study data
                const site = studyData?.sites?.find((s: any) => s.Fields['Site.id'] === item.siteId);
                if (site) element[el.field] = site.Fields[el.subjectField] ?? '';
            } else if (el.field === 'score') {
                element.siteScore = {
                    change:
                        element.siteToShow && item?.previousScore >= 0
                            ? item.previousScore > item.score
                                ? 'lower'
                                : item.previousScore < item.score
                                  ? 'upper'
                                  : ''
                            : '',
                    colour: item?.colour ?? '',
                    label: '',
                    show: element.siteToShow,
                    ...(element.siteToShow ? { score: +item.score.toFixed(2) } : {}),
                };
                element[el.field] = element.siteToShow ? +item.score.toFixed(2) : null;
            } else if (
                el.field === 'amberKris' ||
                el.field === 'redKris' ||
                el.field === 'amberDecisionKris' ||
                el.field === 'redDecisionKris'
            ) {
                element[el.field] = element.siteToShow ? item[el.field] : null;
            } else if (el.fieldName) {
                const KriScore = item?.kriScores.find((elem: any) => el.panelId === elem.panelId);
                element[el.fieldName] = {
                    show: element.siteToShow,
                    change: KriScore?.change ?? '',
                    colour: KriScore?.colour ?? '',
                    label: KriScore?.label,
                    average: KriScore?.average,
                    ...(KriScore && KriScore.score && element.siteToShow ? { score: +KriScore.score.toFixed(2) } : {}),
                };
            } else {
                element[el.field] = item[el.field];
                if (el.field === 'siteActiveDate')
                    element[`_${el.field}`] = formatDateUTC_DEPRECATED(item[el.field], true, 'DD-MMM-YYYY');
            }
        });
        element.status = item.siteStatus;

        return element;
    });

export { generateStringFromData, formatColumn, createRiskOverviewGrid };
